import React from 'react';
import _ from 'lodash';

import { getBillingDashboardPageBreadcrumb } from '../Billing/BillingDashboardPage';

import Field from '../../Fields/Field';
import InputTextField from '../../Fields/InputTextField';
import InputSelectField from '../../Fields/InputSelectField';
import DataTable from '../../Fields/DataTable';

import ActionButton from '../../Common/ActionButton';
import ProcessingIndicator from '../../Common/ProcessingIndicator';
import PageHeader from '../../Common/PageHeader';
import NavigationBreadcrumbs from '../../Common/NavigationBreadcrumbs';

import Subscription from '../../../Entities/Subscription';
import Payor from '../../../Entities/Payor';
import { formatPhoneNumber } from '../../../Entities/util';

import {
  queryGetPayorReport,
  queryListSubscriptions
} from '../../../Queries';

import { getFacility } from '../../../Utils/facility';
import { getRedirectUrl } from '../../../Utils/envUrl';

import PublishIcon from '@material-ui/icons/Publish';

import '../Page.scss';
import './SubscriptionListPage.scss';

import featureFlags from '../../../featureFlags';

const oldSubscriptionQueryFields = [
  'total',
  'limit',
  'offset',
  { 'subscriptions' : [
    'id',
    { 'billing_profile' : [
      'id',
      'name'
    ] },
    { 'payor' : [
      'id',
      'readable_id',
      'type',
      'info_name',
      'billing_firstname',
      'billing_lastname',
      'billing_phone',
      'billing_email'
    ] }
  ] }
];

const subscriptionQueryFields = featureFlags.SUBSCRIPTION_NAME ? [
  'total',
  'limit',
  'offset',
  { 'subscriptions' : [
    'id',
    featureFlags.ACCOUNT_ID ? 'account_id' : '',
    'subscription_name',
    { 'billing_profile' : [
      'id',
      'name'
    ] },
    { 'payor' : [
      'id',
      'readable_id',
      'type',
      'info_name',
      'billing_firstname',
      'billing_lastname',
      'billing_phone',
      'billing_email'
    ] },    
    { 'invoices': [
      'balance'
    ] }
  ] }
]: oldSubscriptionQueryFields;

const oldSubscriptionDataTableColumns = [
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'payor_type', heading: 'Sub. Type' },
  { id: 'payor_billing_name', heading: 'Primary Contact (PC)' },
  { id: 'payor_billing_company', heading: 'Billing Name' },
  { id: 'payor_billing_phone', heading: 'PC Phone' },
  { id: 'payor_billing_email', heading: 'PC Email' },
  { id: 'billing_profile_name', heading: 'Billing Profile' },
  { id: 'actions', heading: null }
];
const newSubscriptionDataTableColumns = featureFlags.SUBSCRIPTION_NAME ? [
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'payor_type', heading: 'Sub. Type' },
  { id: 'subscription_name', heading: 'Sub. Name' },
  { id: 'payor_billing_name', heading: 'Primary Contact (PC)' },
  { id: 'payor_billing_company', heading: 'Billing Name' },
  { id: 'payor_billing_phone', heading: 'PC Phone' },
  { id: 'payor_billing_email', heading: 'PC Email' },
  { id: 'billing_profile_name', heading: 'Billing Profile' },
  { id: 'actions', heading: null }
]:oldSubscriptionDataTableColumns;

const subscriptionDataTableColumns = featureFlags.PAYOR_SEARCH ? [
  { id: 'payor_id', heading: 'Sub. ID' },
  { id: 'payor_type', heading: 'Sub. Type' },
  { id: 'subscription_name', heading: 'Sub. Name' },
  { id: 'payor_billing_name', heading: 'Primary Contact (PC)' },
  { id: 'payor_billing_company', heading: 'Billing Name' },
  { id: 'payor_billing_phone', heading: 'PC Phone' },
  { id: 'payor_billing_email', heading: 'PC Email' },
  { id: 'billing_profile_name', heading: 'Billing Profile' },
  { id: 'balance', heading: 'Account Balance' },
  { id: 'credit', heading: null },
  { id: 'manual_inv', heading: null },
] : !featureFlags.PAYOR_SEARCH && featureFlags.SUBSCRIPTION_NAME ? newSubscriptionDataTableColumns : oldSubscriptionDataTableColumns;

if (featureFlags.ACCOUNT_ID) {
  subscriptionDataTableColumns.splice(4, 1)
  subscriptionDataTableColumns.unshift({ id: 'account_id', heading: 'Acct. ID' });
}

class SubscriptionListPage extends React.Component {
  state = {
    pageInitializationComplete: false,
    pageInitializationErrors: [],
    facilityId: null,
    facility: null,
    subscriptionListQueryInProgress: false,
    subscriptionListQueryErrors: [],
    subscriptionListDataTableRows: [],
    subscriptions: [],
    subscriptionExportQueryInProgress: false,
    subscriptionExportQueryErrors: [],
    subscriptionListFilter: {
      inputs: {
        payor_id: null,
        subscription_name: null,
        status: 'ACTIVE',
        payor_first_name: null,
        payor_last_name: null,
        account_id: null,
        payor_company_name: null
      }
    }
  };

  subscriptionQueryMetadata = {
    sortedBy: null,
    limit: null,
    total: null,
    offset: 0,
  };

  fieldConfiguration = {
    payor: {},
    subscription: {}
  };


  constructor(props) {
    super(props);

    const urlSearchParams = new URLSearchParams(window.location.search);

    const facilityId = urlSearchParams.get('facility');
    this.state.facilityId = facilityId;
  }


  async componentDidMount() {
    try {
      let facility = null;
      if (this.state.facilityId) {
        facility = await getFacility(this.state.facilityId);
      }
  
      const payorData = await Payor.getFieldAllowedValues(['type']);
      this.fieldConfiguration.payor = payorData;
  
      const subscriptionData = await Subscription.getFieldAllowedValues(['status']);
      this.fieldConfiguration.subscription = subscriptionData;

      this.setState({ 
        pageInitializationComplete: true,
        facility
      }, () => {
        this.refreshSubscriptionListDataTable();
      });
    } catch (errors) {
      this.setState({
        pageInitializationComplete: true,
        pageInitializationErrors: errors.map(({ message }) => message)
      });
    }
  }

  async fetchSubscriptions() {
    try {
      const options = this.getSubscriptionQueryOptions();
      const response = await queryListSubscriptions(options, subscriptionQueryFields);

      if (response.errors) {
        throw (response.errors.map(error => error.message));
      }

      this.subscriptionQueryMetadata.total = response.data.listSubscriptions.total;

      const subscriptions = response.data.listSubscriptions.subscriptions.map(subscription => 
        Subscription.createFromQueryValues(subscription)
      );
      return subscriptions;
    } catch (errors) {
      throw Array.isArray(errors) ? errors : [ errors ];
    }
  }


  getSubscriptionQueryOptions() {
    const {
      inputs
    } = this.state.subscriptionListFilter;

    const options = {};

    options.facility_id = this.state.facilityId;

    const filters = [];
    for (let inputId in inputs) {
      if (inputs[inputId] === null) continue;

      switch (inputId) {
        case 'payor_id':
          filters.push({
            model: 'PAYOR',
            field: 'readable_id',
            op: 'EQ',
            value: inputs[inputId]
          });
          break;
        case 'subscription_name':
          filters.push({
            model: 'SUBSCRIPTION',
            field: 'subscription_name',
            op: 'SUBSTRING',
            value: inputs[inputId].trim()
          });
          break;
        case 'account_id':
          filters.push({
            model: 'SUBSCRIPTION',
            field: 'account_id',
            op: 'SUBSTRING',
            value: inputs[inputId].trim().replace(/\D+/g, '')
          });
          break;
        case 'status':
          filters.push({
            model: 'SUBSCRIPTION',
            field: 'status',
            op: 'EQ',
            value: inputs[inputId]
          });
          break;
        case 'payor_first_name':
          filters.push({
            model: 'PAYOR',
            field: 'billing_firstname',
            op: 'SUBSTRING',
            value: inputs[inputId]
          });
          break;
        case 'payor_last_name':
          filters.push({
            model: 'PAYOR',
            field: 'billing_lastname',
            op: 'SUBSTRING',
            value: inputs[inputId]
          });
          break;
        case 'payor_company_name':
          filters.push({
            model: 'PAYOR',
            field: 'info_name',
            op: 'SUBSTRING',
            value: inputs[inputId]
          });
          break;
        default:
      }
    }
    if (filters.length) {
      options.filters = filters;
    }

    if (this.subscriptionQueryMetadata.sortedBy &&
      this.subscriptionQueryMetadata.sortedBy.columnId !== '' &&
      this.subscriptionQueryMetadata.sortedBy.direction !== ''
    ) {
      switch (this.subscriptionQueryMetadata.sortedBy.columnId) {
        case 'billing_profile_name':
          options.order = {
            model: 'BILLING_PROFILE',
            field: 'name',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_type':
          options.order = {
            model: 'PAYOR',
            field: 'type',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'subscription_name':
          options.order = {
            model: 'SUBSCRIPTION',
            field: 'subscription_name',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'account_id':
          options.order = {
            model: 'SUBSCRIPTION',
            field: 'account_id',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_id':
          options.order = {
            model: 'PAYOR',
            field: 'readable_id',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_billing_name':
          options.order = {
            model: 'PAYOR',
            field: 'billing_lastname',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_billing_company':
          options.order = {
            model: 'PAYOR',
            field: 'info_name',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_billing_phone':
          options.order = {
            model: 'PAYOR',
            field: 'billing_phone',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        case 'payor_billing_email':
          options.order = {
            model: 'PAYOR',
            field: 'billing_email',
            direction: this.subscriptionQueryMetadata.sortedBy.direction
          };
          break;
        default:
      }
    }

    if (this.subscriptionQueryMetadata.limit) {
      options.limit = this.subscriptionQueryMetadata.limit;
      options.offset = this.subscriptionQueryMetadata.offset;
    }

    return options;
  }


  refreshSubscriptionListDataTable() {
    this.setState({
      subscriptionListQueryInProgress: true
    }, async () => {
      try {
        const subscriptions = await this.fetchSubscriptions();
        const subscriptionListDataTableRows = this.getSubscriptionListDataTableRows(subscriptions);

        this.setState({
          subscriptionListQueryInProgress: false,
          subscriptionListDataTableRows,
          subscriptions
        });
      } catch (errors) {
        this.setState({
          subscriptionListQueryInProgress: false,
          subscriptionListQueryErrors: errors
        });
      }
    });
  }

  getSubscriptionPageUrl(subscriptionId) {
    const url = getRedirectUrl(window.origin, 'subscription-info');
    return `${url}?subscription=${subscriptionId}&facility=${this.state.facilityId}`;
  }

  getSubscriptionListDataTableRows(subscriptions) {
    const rows = [];
    subscriptions.forEach(subscription => {
      const columns = [];
      const facilityParam = this.state.facilityId ? `&facility=${this.state.facilityId}&subscriptionId=${subscription.fields.id.value}` : ''
      subscriptionDataTableColumns.forEach(column => {
        switch (column.id) {
          case 'billing_profile_name':
            columns.push(subscription.fields.billing_profile.value.fields.name.value);
            break;
          case 'payor_type':
            const typeOption = this.fieldConfiguration.payor.type.options.find(option =>
              option.value === subscription.fields.payor.value.fields.type.value
            );
            columns.push(typeOption ?
              typeOption.label :
              subscription.fields.payor.value.fields.type.value
            );
            break;
          case 'account_id':
            if (featureFlags.REACT_18_SUBSCRIPTION_PAGE) {
              columns.push(`<a href=${this.getSubscriptionPageUrl(subscription.fields.id.value)} target="_blank">AC-${subscription.fields.account_id.value}</a>`);
            } else {
              columns.push(`<a href="admin.php?page=parkchirp_subscription&subscription=${subscription.fields.id.value}${(this.state.facilityId != null) ? `&facility=${this.state.facilityId}` : ''}">AC-${subscription.fields.account_id.value}</a>`);
            }
            break;
          case 'subscription_name':
            columns.push(featureFlags.PAYOR_SEARCH ? `<a href="admin.php?page=parkchirp_subscription&subscription=${subscription.fields.id.value}${(this.state.facilityId != null) ? `&facility=${this.state.facilityId}` : ''}">${subscription.fields.subscription_name.value}</a>` : subscription.fields.subscription_name.value);
            break;
          case 'payor_id':
            columns.push(subscription.fields.payor.value.fields.readable_id.value);
            break;
          case 'payor_billing_name':
            columns.push([
              subscription.fields.payor.value.fields.billing_firstname.value,
              subscription.fields.payor.value.fields.billing_lastname.value
            ].join(' ').trim());
            break;
          case 'payor_billing_company':
            columns.push(subscription.fields.payor.value.fields.info_name.value);
            break;
          case 'payor_billing_phone':
            columns.push(formatPhoneNumber(subscription.fields.payor.value.fields.billing_phone.value));
            break;
          case 'payor_billing_email':
            columns.push(subscription.fields.payor.value.fields.billing_email.value);
            break;
          case 'balance':
          const totalBalance = subscription.getBalance()
            columns.push(`<a href="admin.php?page=parkchirp_subscription&subscription=${subscription.fields.id.value}${(this.state.facilityId != null) ? `&facility=${this.state.facilityId}#/history` : ''}">${(totalBalance / 100).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            })}</a>`);
            break;
          case 'actions':
            !featureFlags.PAYOR_SEARCH && columns.push(`<a href="admin.php?page=parkchirp_subscription&subscription=${subscription.fields.id.value}${(this.state.facilityId != null) ? `&facility=${this.state.facilityId}` : ''}">Manage</a>`);
            break;
          case 'credit':
            featureFlags.PAYOR_SEARCH && columns.push(`<a href="?page=parkchirp_credit${facilityParam}">Credit</a>`);
            break;
          case 'manual_inv':
            featureFlags.PAYOR_SEARCH && columns.push(`<a href="?page=parkchirp_invoice${facilityParam}">Manual Inv.</a>`);
            break;
          default:
        }
      });
      rows.push(columns);
    });
    return rows;
  }


  getSubscriptionDataExport() {
    queryGetPayorReport(this.getSubscriptionQueryOptions())
      .then(response => response.blob())
      .then(csvBlob => {
        if (navigator.msSaveBlob) {
          navigator.msSaveBlob(csvBlob, 'subscriptions.csv');
        }
        else {
          let linkElement = document.createElement('a');
          let url = window.URL;
          let objectUrl;
          linkElement.type = 'text/csv';
          linkElement.download = 'subscriptions.csv';
          linkElement.href = objectUrl = url.createObjectURL(csvBlob);
          linkElement.dispatchEvent(new MouseEvent('click'));
          setTimeout(url.revokeObjectURL(objectUrl));
        }
      });
  }



  // -- Subscription List Filter Form Actions & Event Handlers


  _subscriptionListFilterInputOnChange(inputId) {
    return value => {
      const inputs = _.cloneDeep(this.state.subscriptionListFilter.inputs);
      inputs[inputId] = value;
      const subscriptionListFilter = {
        ...this.state.subscriptionListFilter,
        inputs
      };
      this.setState({ subscriptionListFilter });
    };
  }


  resetSubscriptionListFilterForm() {
    const inputs = {};
    for (let inputId in this.state.subscriptionListFilter.inputs) {
      switch (inputId) {
        case 'status':
          inputs[inputId] = 'ACTIVE';
          break;
        default:
          inputs[inputId] = null;
      }
    }
    const subscriptionListFilter = {
      ...this.state.subscriptionListFilter,
      inputs
    };
    this.setState({
      subscriptionListFilter
    }, () => {
      this.refreshSubscriptionListDataTable();
    });
  }


  handleSubscriptionListFilterFilterClick = () => {
    this.refreshSubscriptionListDataTable();
  }


  handleSubscriptionListFilterResetClick = () => {
    this.resetSubscriptionListFilterForm();
  }



  // -- Subscription List Data Table Event Handlers


  handleSubscriptionListExportToCSVClick = () => {
    this.getSubscriptionDataExport();
  }


  handleSubscriptionListDataTableSortRequest = data => {
    if(data?.columnId === 'balance') {
      let rawValues = _.cloneDeep(this.state.subscriptions);
      const values = rawValues.sort((a, b) => { 
        const diff = a.getBalance() - b.getBalance();
        return (data.direction === 'ASC' ? diff : -diff);
      });
      const subscriptionListDataTableRows = this.getSubscriptionListDataTableRows(values)
      this.setState({ subscriptionListDataTableRows })
    } else {
      this.refreshSubscriptionListDataTable();
    }
    this.subscriptionQueryMetadata.sortedBy = data;
  }


  handleSubscriptionListDataTableLimitChange = data => {
    this.subscriptionQueryMetadata.limit = data;
    this.subscriptionQueryMetadata.offset = 0;
    this.refreshSubscriptionListDataTable();
  }


  handleSubscriptionListDataTablePageChange = data => {
    const offset = ((data - 1) * this.subscriptionQueryMetadata.limit);
    this.subscriptionQueryMetadata.offset = offset;
    this.refreshSubscriptionListDataTable();
  }



  // -- Rendering


  renderNavigationBreadcrumbs() {
    const breadcrumbs = [];
    breadcrumbs.push(getBillingDashboardPageBreadcrumb(false, this.state.facilityId));
    breadcrumbs.push(getSubscriptionListPageBreadcrumb());

    return (
      <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
    );
  }

  getBulkUploadUrl(parkersOnly = 0) {
    const url = getRedirectUrl(window.origin, 'bulk-upload');
    return `${url}&parkersOnly=${parkersOnly}&facility=${this.state.facilityId}`;
  }

  renderPageHeader() {
    const {
      facilityId,
      facility
    } = this.state;

    const heading = 'Subscriptions';
    const subheading = (facility === null) ? null :
      facility.fields.facility_name.value;
    const actions = [
      <a href={`?page=parkchirp_subscription` +
        `${(facilityId !== null) ? `&facility=${facilityId}` : ''}`}
        className="button button-primary">
        Create New Subscription
      </a>
    ];
    const actions2 = [
      <a href={this.getBulkUploadUrl()} className="button button-secondary subscription-upload-button">
        <div>
          <div>
            <PublishIcon fontSize="large" style={{color: "black"}} />
          </div>
          <div className="subscription-upload-button--text">
            Subscriptions <br/>& Parkers
          </div>
        </div>
      </a>,
      <a href={this.getBulkUploadUrl(1)} className="button button-secondary subscription-upload-button">
        <div>
          <div>
            <PublishIcon fontSize="large" style={{color: "black"}} />
          </div>
          <div className="subscription-upload-button--text">
            Parkers to Existing Accounts
          </div>
        </div>
      </a>
    ];

    return (
      <PageHeader heading={heading}
        subheading={subheading}
        actions={actions}
        actions2={actions2} />
    );
  }


  renderPageInitializationBlock() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <React.Fragment>
        {(!pageInitializationComplete) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Initializing, please wait..." />
          </div>
        }
        {(pageInitializationComplete && pageInitializationErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while initializing the page:</p>
              <ul>
                {pageInitializationErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }


  renderSubscriptionListFilterForm() {
    const {
      subscriptionListQueryInProgress
    } = this.state;

    const {
      inputs
    } = this.state.subscriptionListFilter;

    return (
      <div className="parkchirp--subscription-list--filter-form-container">
        <div className="parkchirp--subscription-list--filter-form">
          <h2>Filter</h2>
          <div className="parkchirp--subscription-list--filter-inputs">
            <div className="parkchirp--subscription-list--filter-input-block">
              {featureFlags.ACCOUNT_ID && 
                <Field label="Account ID"
                  id="parkchirp--subscription-list-filter--account-id"
                  layout="columns">
                  <InputTextField inputId="parkchirp--subscription-list-filter--account-id"
                    value={inputs.account_id}
                    onChange={this._subscriptionListFilterInputOnChange('account_id')}
                    editMode={true}
                  />
                </Field>
              }
              <Field label="Sub. ID"
                id="parkchirp--subscription-list-filter--payor-id"
                layout="columns">
                <InputTextField inputId="parkchirp--subscription-list-filter--payor-id"
                  value={inputs.payor_id}
                  onChange={this._subscriptionListFilterInputOnChange('payor_id')}
                  editMode={true}
                />
              </Field>

              {!featureFlags.SUBSCRIPTION_NAME && 
                <Field label="Status"
                  id="parkchirp--subscription-list-filter--status"
                  layout="columns">
                  <InputSelectField inputId="parkchirp--subscription-list-filter-status"
                    options={[ { value: null, label: 'All' }, ...this.fieldConfiguration.subscription.status.options ]}
                    value={inputs.status}
                    onChange={this._subscriptionListFilterInputOnChange('status')}
                    editMode={true}
                  />
                </Field>
              }

              {featureFlags.SUBSCRIPTION_NAME && !featureFlags.ACCOUNT_ID &&
                <Field label="Sub. Name"
                  id="parkchirp--subscription-list-filter--sub-name"
                  layout="columns">
                  <InputTextField inputId="parkchirp--subscription-list-filter--sub-name"
                    value={inputs.subscription_name}
                    onChange={this._subscriptionListFilterInputOnChange('subscription_name')}
                    editMode={true}
                  />
                </Field>
              }

            </div>
            <div className="parkchirp--subscription-list--filter-input-block">
              <Field label="PC First Name"
                id="parkchirp--subscription-list-filter--billing-firstname"
                layout="columns">
                <InputTextField inputId="parkchirp--subscription-list-filter--billing-firstname"
                  value={inputs.payor_first_name}
                  onChange={this._subscriptionListFilterInputOnChange('payor_first_name')}
                  editMode={true}
                />
              </Field>
              <Field label="PC Last Name"
                id="parkchirp--subscription-list-filter--billing-lastname"
                layout="columns">
                <InputTextField inputId="parkchirp--subscription-list-filter--billing-lastname"
                  value={inputs.payor_last_name}
                  onChange={this._subscriptionListFilterInputOnChange('payor_last_name')}
                  editMode={true}
                />
              </Field>
            </div>
            <div className="parkchirp--subscription-list--filter-input-block">
              {!featureFlags.ACCOUNT_ID && 
                <Field label="Billing Name"
                  id="parkchirp--subscription-list-filter--company-name"
                  layout="columns">
                  <InputTextField inputId="parkchirp--subscription-list-filter--company-name"
                    value={inputs.payor_company_name}
                    onChange={this._subscriptionListFilterInputOnChange('payor_company_name')}
                    editMode={true}
                  />
                </Field>
              }
              {featureFlags.SUBSCRIPTION_NAME && featureFlags.ACCOUNT_ID &&
                <Field label="Sub. Name"
                  id="parkchirp--subscription-list-filter--sub-name"
                  layout="columns">
                  <InputTextField inputId="parkchirp--subscription-list-filter--sub-name"
                    value={inputs.subscription_name}
                    onChange={this._subscriptionListFilterInputOnChange('subscription_name')}
                    editMode={true}
                  />
                </Field>
              }
              {(featureFlags.SUBSCRIPTION_NAME || featureFlags.ACCOUNT_ID) && 
                <Field label="Status"
                  id="parkchirp--subscription-list-filter--status"
                  layout="columns">
                  <InputSelectField inputId="parkchirp--subscription-list-filter-status"
                    options={[ { value: null, label: 'All' }, ...this.fieldConfiguration.subscription.status.options ]}
                    value={inputs.status}
                    onChange={this._subscriptionListFilterInputOnChange('status')}
                    editMode={true}
                  />
                </Field>
              }

            </div>
          </div>
          <div className="parkchirp--subscription-list--filter-actions">
            <button type="button"
              className="button button-primary parkchirp--subscription-list--filter-action"
              onClick={this.handleSubscriptionListFilterFilterClick}
              disabled={subscriptionListQueryInProgress}>
              Filter
            </button>
            <button type="button"
              className="button parkchirp--subscription-list--filter-action"
              onClick={this.handleSubscriptionListFilterResetClick}
              disabled={subscriptionListQueryInProgress}>
              Reset
            </button>
          </div>
        </div>
      </div>
    );
  }


  renderSubscriptionListDataTable() {
    const {
      subscriptionListQueryInProgress,
      subscriptionListQueryErrors,
      subscriptionListDataTableRows,
      subscriptionExportQueryInProgress
    } = this.state;

    return (
      <React.Fragment>
        {(subscriptionListQueryInProgress) &&
          <div className="parkchirp--page--user-message-container">
            <ProcessingIndicator userMessage="Retrieving data, please wait..." />
          </div>
        }
        {(!subscriptionListQueryInProgress && subscriptionListQueryErrors.length > 0) &&
          <div className="parkchirp--page--user-message-container">
            <div className="parkchirp--page--user-message">
              <p>The following errors occurred while retrieving the Subscription data:</p>
              <ul>
                {subscriptionListQueryErrors.map(error =>
                  <li key={performance.now()}>{error}</li>
                )}
              </ul>
            </div>
          </div>
        }
        {(!subscriptionListQueryInProgress && subscriptionListQueryErrors.length === 0) &&
          <React.Fragment>
            <div className="parkchirp--subscription-list-data-table-actions">
              {(this.subscriptionQueryMetadata.total !== null && this.subscriptionQueryMetadata.total > 0) &&
                <ActionButton label="Export to CSV"
                  inProgressState={subscriptionExportQueryInProgress}
                  onClick={this.handleSubscriptionListExportToCSVClick}
                />
              }
            </div>
            <DataTable
              id="subscription-list"
              rows={subscriptionListDataTableRows}
              columns={subscriptionDataTableColumns}
              overallRowCount={this.subscriptionQueryMetadata.total}
              currentOffset={this.subscriptionQueryMetadata.offset}
              currentLimit={this.subscriptionQueryMetadata.limit}
              sortedBy={this.subscriptionQueryMetadata.sortedBy}
              onSortRequest={this.handleSubscriptionListDataTableSortRequest}
              onLimitChange={this.handleSubscriptionListDataTableLimitChange}
              onPageChange={this.handleSubscriptionListDataTablePageChange}
            />
          </React.Fragment>
        }
      </React.Fragment>
    );
  }


  render() {
    const {
      pageInitializationComplete,
      pageInitializationErrors
    } = this.state;

    return (
      <div className="parkchirp--page parkchirp--page--subscription-list">
        {this.renderNavigationBreadcrumbs()}
        {this.renderPageHeader()}
        <div className="parkchirp--page-content">
          {(!pageInitializationComplete || pageInitializationErrors.length > 0) &&
            this.renderPageInitializationBlock()
          }
          {(pageInitializationComplete && pageInitializationErrors.length === 0) &&
            <React.Fragment>
              {this.renderSubscriptionListFilterForm()}
              {this.renderSubscriptionListDataTable()}
            </React.Fragment>
          }
        </div>
      </div>
    );
  }
}
export default SubscriptionListPage;


export function getSubscriptionListPageBreadcrumb(currentPage = true, facilityId = null) {
  const title = 'Subscriptions';
  const href = (currentPage) ? null :
    `?page=parkchirp_subscriptions${(facilityId !== null) ? `&facility=${facilityId}` : ''}`;

  return { title, href };
}
